import { FC, KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CakeIcon,
  CollectionIcon,
  DatabaseIcon,
  DeviceMobileIcon,
  IdentificationIcon,
  LocationMarkerIcon,
  MailIcon,
  RefreshIcon,
  ShieldCheckIcon,
  SpeakerphoneIcon,
  TagIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/outline';
import { twMerge } from 'tailwind-merge';
import { showToast } from '@/features/toasts/toastsSlice';
import {
  aggregateTopValues,
  buildCoverageRows,
  discoverTopValueTraits,
  formatTraitLabel,
  getDefaultDistributionTrait,
  getDistributionCoveragePct,
  getVisibleCoverageRows,
} from '@/features/marketIntelligence/utils/dataProfileStats';
import { useWorkspace } from '@/features/workspaces/hooks';
import { BaseBadge, Button, Card } from '@/lib';
import { ToastType } from '@/models/toast';
import { useAppDispatch } from '@/reducers';
import { useGetMarketIntelligenceDataProfileQuery, useRefreshMarketIntelligenceDataProfileMutation } from '@/services/endpoints/marketIntelligence';

type CoverageLevel = 'all' | 'partial' | 'none';

type DistributionItem = {
  label: string;
  percent: number;
};

type CoverageRowItem = {
  id: string;
  icon: typeof MailIcon;
  coverage: CoverageLevel;
};

const TRAIT_ICONS: Record<string, typeof MailIcon> = {
  email: MailIcon,
  name: IdentificationIcon,
  birthday: CakeIcon,
  address: LocationMarkerIcon,
  gender: UserIcon,
  phone: DeviceMobileIcon,
  country: LocationMarkerIcon,
  city: LocationMarkerIcon,
  event: CollectionIcon,
};

const formatNumber = (value: number, language: string) => new Intl.NumberFormat(language || 'en').format(value);

const formatDateTime = (value: string | null | undefined, language: string) => {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(language || 'en', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
};

const getTraitIcon = (traitId: string) => TRAIT_ICONS[traitId] ?? TagIcon;

const SummaryCard: FC<{
  icon: typeof DatabaseIcon;
  value: number;
  title: string;
  description: string;
  language: string;
}> = ({ icon: Icon, value, title, description, language }) => (
  <Card className='block rounded-lg border-primary-border bg-white p-5 drop-shadow-none'>
    <div className='mb-5 flex size-10 items-center justify-center rounded-lg bg-brand-50 text-brand-600'>
      <Icon className='size-5' />
    </div>
    <p className='text-primary-900 text-4xl font-semibold leading-none'>{formatNumber(value, language)}</p>
    <p className='text-primary-900 mt-3 text-sm font-semibold'>{title}</p>
    <p className='text-primary-500 mt-1 text-xs leading-5'>{description}</p>
  </Card>
);

const StatusBadge: FC<{ tone: 'success' | 'warning' | 'muted'; children: string }> = ({ tone, children }) => (
  <BaseBadge
    className={twMerge(
      'inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold',
      tone === 'success' && 'border border-green-200 bg-green-50 text-green-700',
      tone === 'warning' && 'border border-orange-200 bg-orange-50 text-orange-700',
      tone === 'muted' && 'border border-primary-border bg-primary-50 text-primary-600'
    )}
  >
    <span
      className={twMerge(
        'size-1.5 rounded-full',
        tone === 'success' && 'bg-green-500',
        tone === 'warning' && 'bg-orange-500',
        tone === 'muted' && 'bg-primary-300'
      )}
    />
    {children}
  </BaseBadge>
);

const AvailabilityRow: FC<{
  item: CoverageRowItem;
  title: string;
  description: string;
  badgeLabel: string;
}> = ({ item, title, description, badgeLabel }) => {
  const Icon = item.icon;
  const badgeTone = item.coverage === 'all' ? 'success' : 'muted';

  return (
    <div className='flex items-center gap-4 rounded-lg border border-primary-border bg-white p-3'>
      <div className='flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary-border bg-white-100 text-primary-500'>
        <Icon className='size-5' />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='text-primary-900 truncate text-sm font-semibold'>{title}</p>
        <p className='text-primary-500 truncate text-xs'>{description}</p>
      </div>
      <StatusBadge tone={badgeTone}>{badgeLabel}</StatusBadge>
    </div>
  );
};

const TRAIT_DISTRIBUTION_PANEL_ID = 'trait-distribution-panel';

type TraitDistributionTabsProps = {
  traits: string[];
  value: string | null;
  onChange: (trait: string) => void;
  getLabel: (trait: string) => string;
  ariaLabel: string;
};

const TraitDistributionTabs: FC<TraitDistributionTabsProps> = ({ traits, value, onChange, getLabel, ariaLabel }) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    event.preventDefault();

    const nextIndex = event.key === 'ArrowLeft' ? (index - 1 + traits.length) % traits.length : (index + 1) % traits.length;

    onChange(traits[nextIndex]);
  };

  return (
    <div role='tablist' aria-label={ariaLabel} className='inline-flex h-9 max-w-full items-center gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1'>
      {traits.map((trait, index) => {
        const isSelected = trait === value;

        return (
          <button
            key={trait}
            type='button'
            role='tab'
            id={`trait-tab-${trait}`}
            aria-selected={isSelected}
            aria-controls={TRAIT_DISTRIBUTION_PANEL_ID}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(trait)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={twMerge(
              'inline-flex h-7 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-md px-3 text-sm font-medium transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2',
              isSelected ? 'bg-white-100 text-primary-900 shadow-sm' : 'bg-transparent text-primary-500 hover:text-primary-900'
            )}
          >
            {getLabel(trait)}
          </button>
        );
      })}
    </div>
  );
};

const DistributionBars: FC<{
  items: DistributionItem[];
  emptyLabel: string;
}> = ({ items, emptyLabel }) => {
  if (!items.length) {
    return <p className='text-primary-500 rounded-lg border border-dashed border-primary-border p-4 text-sm'>{emptyLabel}</p>;
  }

  return (
    <div className='space-y-4'>
      {items.map((item) => (
        <div key={item.label}>
          <div className='text-primary-700 mb-1 flex items-center justify-between gap-3 text-sm'>
            <span className='truncate'>{item.label}</span>
            <span className='text-primary-500 text-xs'>{item.percent}%</span>
          </div>
          <div className='h-2 w-full overflow-hidden rounded-full bg-gray-100'>
            <div className='h-full rounded-full bg-brand-600' style={{ width: `${item.percent}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const MarketIntelligenceDataProfile: FC = () => {
  const { i18n, t } = useTranslation('marketIntelligence');
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const [selectedDistributionTrait, setSelectedDistributionTrait] = useState<string | null>(null);
  const [showAllCoverageRows, setShowAllCoverageRows] = useState(false);
  const [refreshingUserId, setRefreshingUserId] = useState<string | null>(null);
  const {
    data: profile,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useGetMarketIntelligenceDataProfileQuery(
    { workspaceId: workspace.id },
    {
      pollingInterval: 0,
    }
  );
  const [refreshDataProfile, { isLoading: isRefreshing }] = useRefreshMarketIntelligenceDataProfileMutation();
  const shouldPoll = profile?.refreshStatus === 'RUNNING';

  useEffect(() => {
    if (!shouldPoll) {
      setRefreshingUserId(null);
      return undefined;
    }

    const pollingInterval = window.setInterval(() => {
      refetch();
    }, 4000);

    return () => window.clearInterval(pollingInterval);
  }, [refetch, shouldPoll]);

  const payload = profile?.payload ?? null;
  const generatedAt = formatDateTime(profile?.generatedAt || payload?.generatedAt, i18n.language);
  const datasources = payload?.datasources ?? [];

  const distributionTraits = useMemo(() => discoverTopValueTraits(datasources), [datasources]);

  useEffect(() => {
    if (!distributionTraits.length) {
      setSelectedDistributionTrait(null);
      return;
    }

    setSelectedDistributionTrait((current) => {
      if (current && distributionTraits.includes(current)) {
        return current;
      }

      return getDefaultDistributionTrait(datasources);
    });
  }, [datasources, distributionTraits]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'datasources',
        icon: DatabaseIcon,
        value: payload?.summary.totalDatasources ?? payload?.datasources.length ?? 0,
      },
      {
        id: 'audiences',
        icon: UserGroupIcon,
        value: payload?.summary.totalInUseAudiences ?? payload?.inUseAudiences.length ?? 0,
      },
      {
        id: 'campaigns',
        icon: SpeakerphoneIcon,
        value: payload?.summary.totalActiveCampaigns ?? payload?.activeCampaigns.length ?? 0,
      },
      {
        id: 'channels',
        icon: ShieldCheckIcon,
        value: payload?.summary.totalConnectedDestinations ?? payload?.connectedDestinations.length ?? 0,
      },
    ],
    [payload]
  );

  const coverageRows = useMemo(() => buildCoverageRows(datasources), [datasources]);
  const visibleCoverageRows = useMemo(() => getVisibleCoverageRows(coverageRows, showAllCoverageRows), [coverageRows, showAllCoverageRows]);

  const coverageItems = useMemo<CoverageRowItem[]>(
    () =>
      visibleCoverageRows.map((row) => ({
        id: row.id,
        icon: getTraitIcon(row.id),
        coverage: row.coverage,
      })),
    [visibleCoverageRows]
  );

  const distributionItems = useMemo(
    () => (selectedDistributionTrait ? aggregateTopValues(datasources, selectedDistributionTrait) : []),
    [datasources, selectedDistributionTrait]
  );

  const distributionCoveragePct = useMemo(
    () => (selectedDistributionTrait ? getDistributionCoveragePct(datasources, selectedDistributionTrait) : null),
    [datasources, selectedDistributionTrait]
  );

  const getTraitTitle = (traitId: string) => {
    const translationKey = `dataProfile.traits.${traitId}.title`;
    const translated = t(translationKey);

    return translated === translationKey ? formatTraitLabel(traitId) : translated;
  };

  const getTraitDescription = (traitId: string) => {
    const translationKey = `dataProfile.traits.${traitId}.description`;
    const translated = t(translationKey);

    return translated === translationKey ? t('dataProfile.traits.defaultDescription') : translated;
  };

  const handleRefresh = async () => {
    try {
      const result = await refreshDataProfile({ workspaceId: workspace.id }).unwrap();

      if ('status' in result && result.status === 'RUNNING') {
        setRefreshingUserId(result.startedByUserId);
      }

      refetch();
    } catch {
      dispatch(showToast({ type: ToastType.ERROR, title: t('dataProfile.toast.refreshFailedTitle'), message: t('dataProfile.toast.refreshFailedMessage') }));
    }
  };

  const isRunning = profile?.refreshStatus === 'RUNNING' || Boolean(refreshingUserId);
  const bannerText = (() => {
    if (isRunning) {
      return payload
        ? t('dataProfile.banner.running', { userId: refreshingUserId || profile?.refreshByUserId || t('dataProfile.someone') })
        : t('dataProfile.banner.firstRunning');
    }

    if (!payload) {
      return t('dataProfile.banner.firstRefresh');
    }

    if (profile?.stale) {
      return t('dataProfile.banner.stale');
    }

    if (payload.partial) {
      return t('dataProfile.banner.partial');
    }

    return t('dataProfile.banner.ready');
  })();

  if (isLoading) {
    return (
      <Card className='block rounded-lg border-primary-border bg-white p-6 drop-shadow-none'>
        <div className='py-12 text-center'>
          <h3 className='mb-2 text-lg font-semibold text-primary-900'>{t('dataProfile.loading.title')}</h3>
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className='block rounded-lg border-primary-border bg-white p-6 drop-shadow-none'>
        <div className='flex flex-col gap-4 py-10 text-center'>
          <div>
            <h3 className='mb-2 text-lg font-semibold text-primary-900'>{t('dataProfile.error.title')}</h3>
            <p className='text-primary-600'>{t('dataProfile.error.description')}</p>
          </div>
          <Button intent='secondary' size='md' className='mx-auto' onClick={() => refetch()}>
            <RefreshIcon className='size-4' />
            <span>{t('dataProfile.refresh')}</span>
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className='w-full space-y-5'>
      <Card className='block rounded-lg border-primary-border bg-white p-6 drop-shadow-none'>
        <div className='flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between'>
          <div>
            <h2 className='text-primary-900 text-2xl font-semibold'>{t('dataProfile.header.title')}</h2>
            <p className='text-primary-600 mt-2 text-sm'>{t('dataProfile.header.description')}</p>
          </div>
          <div className='flex flex-col items-start gap-3 sm:flex-row sm:items-center lg:items-end'>
            <div className='text-left sm:text-right'>
              <p className='text-primary-500 text-[11px] font-semibold uppercase tracking-[0.08em]'>{t('dataProfile.lastUpdated')}</p>
              <p className='text-primary-900 text-sm font-semibold'>{generatedAt || t('dataProfile.neverUpdated')}</p>
            </div>
            {profile?.stale ? <StatusBadge tone='warning'>{t('dataProfile.needsRefresh')}</StatusBadge> : null}
            <Button
              intent='primary'
              size='md'
              onClick={handleRefresh}
              disabled={isRunning || isRefreshing}
              isLoading={isRefreshing || (isFetching && isRunning)}
            >
              <RefreshIcon className='size-4' />
              <span>{isRunning ? t('dataProfile.refreshing') : t('dataProfile.refresh')}</span>
            </Button>
          </div>
        </div>

        <div className='bg-primary-50 mt-8 flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-primary-600'>
          <ShieldCheckIcon className={twMerge('size-5 shrink-0', profile?.stale || !payload ? 'text-orange-500' : 'text-green-600')} />
          <span>{bannerText}</span>
        </div>
      </Card>

      <div className='grid gap-5 md:grid-cols-2 xl:grid-cols-4'>
        {summaryCards.map((card) => (
          <SummaryCard
            key={card.id}
            icon={card.icon}
            value={card.value}
            title={t(`dataProfile.summary.${card.id}.title`)}
            description={t(`dataProfile.summary.${card.id}.description`)}
            language={i18n.language}
          />
        ))}
      </div>

      <Card className='block rounded-lg border-primary-border bg-white p-0 drop-shadow-none'>
        <div className='border-primary-border border-b p-6'>
          <h3 className='text-primary-900 text-lg font-semibold'>{t('dataProfile.contacts.title')}</h3>
          <p className='text-primary-600 mt-2 text-sm'>{t('dataProfile.contacts.description')}</p>
        </div>

        <div className='grid lg:grid-cols-2'>
          <div className='border-primary-border p-6 lg:border-r'>
            <div className='mb-5 space-y-3'>
              <p className='text-primary-600 text-xs font-bold uppercase tracking-[0.08em]'>{t('dataProfile.contacts.traitBreakdown')}</p>
              {distributionTraits.length ? (
                <TraitDistributionTabs
                  traits={distributionTraits}
                  value={selectedDistributionTrait}
                  onChange={setSelectedDistributionTrait}
                  getLabel={getTraitTitle}
                  ariaLabel={t('dataProfile.contacts.traitSelectorLabel')}
                />
              ) : null}
            </div>
            <div
              id={TRAIT_DISTRIBUTION_PANEL_ID}
              role='tabpanel'
              aria-labelledby={selectedDistributionTrait ? `trait-tab-${selectedDistributionTrait}` : undefined}
            >
              <DistributionBars items={distributionItems} emptyLabel={t('dataProfile.contacts.noDistributions')} />
            </div>
            <p className='text-primary-500 mt-5 text-xs'>
              {distributionCoveragePct !== null
                ? t('dataProfile.contacts.distributionHintWithCoverage', { coverage: distributionCoveragePct })
                : t('dataProfile.contacts.distributionHint')}
            </p>
          </div>

          <div className='p-6'>
            <p className='text-primary-600 mb-5 text-xs font-bold uppercase tracking-[0.08em]'>{t('dataProfile.contacts.dataCompleteness')}</p>
            <div className='space-y-3'>
              {coverageItems.map((item) => (
                <AvailabilityRow
                  key={item.id}
                  item={item}
                  title={getTraitTitle(item.id)}
                  description={getTraitDescription(item.id)}
                  badgeLabel={t(`dataProfile.coverage.${item.coverage}`)}
                />
              ))}
            </div>
            {coverageRows.length > 8 ? (
              <button
                type='button'
                className='text-brand-600 mt-4 text-sm font-semibold hover:underline'
                onClick={() => setShowAllCoverageRows((current) => !current)}
              >
                {showAllCoverageRows ? t('dataProfile.contacts.showLessTraits') : t('dataProfile.contacts.showMoreTraits')}
              </button>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  );
};
